// Trạng thái chung của trang sơ đồ mạng lưới (/npp/network, /network): ô tìm, bộ lọc (Tất cả / Cần xử lý / ô loại
// trên dải thống kê), ô đang chọn, ngăn chi tiết. Hai trang khác dữ liệu (API khác nhau) nhưng hành vi phải như một.
//  · Đơn vị đang xem nằm trên URL (?unit=) — gửi link là mở đúng chỗ; ô gốc (NPP / chính mình) thì không lên URL.
//  · Bấm lại ô đang chọn = đóng ngăn.
//  · Điện thoại / iPad (cây thụt lề): nhánh mở / gập từng ô, mặc định mở tới cấp 1; chọn đơn vị thì mở sẵn chuỗi cấp
//    trên (xoá ô tìm vẫn thấy ô vừa chọn).
//  · Máy tính (`desktop`, sơ đồ theo nhóm — @shoptrack/common/network-drill): đường đang mở + lịch sử cho nút Quay lại;
//    đi nhánh khác thì đóng ngăn; tìm / lọc thì hiện cột kết quả. Đơn vị đang chọn mà chưa hiện trên sơ đồ (link
//    ?unit=, bấm trong ngăn, vừa thoát tìm) thì mở đường tới nó.
// `hasIssue` / `searchFields` phải là hàm cố định (khai ở ngoài component) — nằm trong phụ thuộc của useMemo.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  networkAncestors, networkCodeOrder, networkMatcher, networkOpenToDepth,
  type NetworkNode, type NetworkTree, type NetworkUnitBase, type NetworkUnitType,
} from '@shoptrack/common/network-tree'
import {
  NETWORK_DRILL_PAGE, NETWORK_DRILL_RESULTS, NETWORK_DRILL_STEP,
  networkDrillClick, networkDrillColumns, networkDrillIndex, networkDrillOpenTo, networkDrillReveal, networkDrillSame,
  networkDrillShows, networkDrillSoleGroup, networkDrillStart,
  type NetworkDrillItem, type NetworkDrillState,
} from '@shoptrack/common/network-drill'
import { networkFilterMatch, networkStripCounts, nextNetworkFilter, type NetworkFilter } from './network-view'
import { NETWORK_TYPE_META } from './NetworkTreeRow'

const TYPE_ORDER: Record<NetworkUnitType, number> = { agency: 0, ctv: 1, shop: 2 }
/** Quay lại được tối đa chừng này bước. */
const HISTORY_MAX = 50

export function useNetworkTreeState<U extends NetworkUnitBase & { orders30d?: number | null }>(
  tree: NetworkTree<U>,
  {
    rootKey,
    desktop,
    hasIssue,
    searchFields,
  }: {
    /** Khoá ô gốc trên sơ đồ — không trùng mã đơn vị. */
    rootKey: string
    /** Máy tính — sơ đồ theo nhóm (điện thoại / iPad là cây thụt lề). */
    desktop: boolean
    hasIssue: (unit: U) => boolean
    /** Chữ ô tìm so khớp ngoài tên đơn vị (tên chủ, SĐT, mã bảng giá…). */
    searchFields: (unit: U) => Array<string | null | undefined>
  },
) {
  const [params, setParams] = useSearchParams()
  // null = mặc định: mở tới cấp 1.
  const [openIds, setOpenIds] = useState<Set<string> | null>(null)
  const open = useMemo(() => openIds ?? networkOpenToDepth(tree, 1), [openIds, tree])
  const [text, setText] = useState('')
  const [filter, setFilter] = useState<NetworkFilter>('all')
  const [rootOpen, setRootOpen] = useState(false)

  const stats = useMemo(() => networkStripCounts(tree, hasIssue), [tree, hasIssue])
  const match = useMemo(() => networkFilterMatch(filter, networkMatcher<U>(text, searchFields), hasIssue), [text, filter, searchFields, hasIssue])
  const filtering = !!match
  const hits = useMemo(() => (match ? [...tree.byId.values()].filter((n) => match(n.unit)).length : 0), [tree, match])

  const selId = params.get('unit')
  const sel = selId ? tree.byId.get(selId) ?? null : null
  const selKey = sel ? sel.unit.id : rootOpen ? rootKey : null
  // Ngăn đóng vẫn giữ nội dung cũ trong lúc trượt ra
  const lastKey = useRef<string | null>(null)
  if (selKey) lastKey.current = selKey
  const shownKey = selKey ?? lastKey.current
  const shownNode = shownKey && shownKey !== rootKey ? tree.byId.get(shownKey) ?? null : null

  const setUnitParam = useCallback(
    (id: string | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p)
          if (id) next.set('unit', id)
          else next.delete('unit')
          return next
        },
        { replace: true },
      ),
    [setParams],
  )
  const close = useCallback(() => {
    setRootOpen(false)
    setUnitParam(null)
  }, [setUnitParam])

  // ─── Sơ đồ theo nhóm (máy tính) ──────────────────────────────
  const ix = useMemo(() => networkDrillIndex(tree, rootKey, (u) => Number(u.orders30d) || 0), [tree, rootKey])
  const start = useMemo(() => networkDrillStart(ix), [ix])
  // null = lúc mới mở trang (gốc chỉ có một loại cấp dưới thì nhóm đó mở sẵn — cây tải xong mới biết)
  const [drillRaw, setDrillRaw] = useState<NetworkDrillState | null>(null)
  const drill = drillRaw ?? start
  const [hist, setHist] = useState<NetworkDrillState[]>([])
  // Số kết quả đang hiện — đổi chữ tìm / bộ lọc là về trang đầu
  const [resPage, setResPage] = useState<{ match: typeof match; shown: number }>({ match: undefined, shown: NETWORK_DRILL_PAGE })
  const resShown = resPage.match === match ? resPage.shown : NETWORK_DRILL_PAGE
  const results = useMemo(() => {
    if (!desktop || !match) return null
    return [...tree.byId.values()]
      .filter((n) => match(n.unit))
      .sort((a, b) => (TYPE_ORDER[a.unit.tenantType] ?? 2) - (TYPE_ORDER[b.unit.tenantType] ?? 2) || networkCodeOrder(a.unit, b.unit) || ix.act(b) - ix.act(a) || a.unit.name.localeCompare(b.unit.name, 'vi'))
  }, [desktop, match, tree, ix])
  const columns = useMemo(() => networkDrillColumns(ix, drill, results && { units: results, shown: resShown }), [ix, drill, results, resShown])

  const clearFilters = () => {
    setText('')
    setFilter('all')
  }
  /** Một bước mới trên sơ đồ — bước cũ vào lịch sử cho nút Quay lại. `keepPanel`: ngăn chi tiết giữ nguyên. */
  const go = (next: NetworkDrillState, keepPanel = false) => {
    if (!networkDrillSame(next, drill)) {
      setHist((h) => [...h.slice(-(HISTORY_MAX - 1)), drill])
      setDrillRaw(next)
    }
    if (!keepPanel) close()
  }

  // Đơn vị đang chọn chưa hiện trên sơ đồ (mở link ?unit=, cây vừa tải xong, vừa thoát tìm / lọc) → mở đường tới nó,
  // không tính là một bước
  useEffect(() => {
    if (!desktop || !sel || networkDrillShows(columns, sel.unit.id)) return
    setDrillRaw(networkDrillReveal(ix, sel))
    // Chỉ khi đổi đơn vị / cây tải lại / thoát tìm — đi nhánh khác sau đó thì ngăn đã đóng
  }, [sel, desktop, filtering]) // eslint-disable-line react-hooks/exhaustive-deps

  // Điện thoại: mở link ?unit= của đơn vị nằm trong nhánh đang gập → mở sẵn chuỗi cấp trên để thấy hàng.
  useEffect(() => {
    if (desktop || !sel) return
    const ancestors = networkAncestors(sel)
    if (ancestors.some((a) => !open.has(a.unit.id))) setOpenIds(new Set([...open, ...ancestors.map((a) => a.unit.id)]))
    // Chỉ khi đổi đơn vị / cây tải lại — gập tay một cấp trên sau đó thì để nguyên
  }, [sel, desktop]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => setOpenIds(() => {
    const next = new Set(open)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const select = (key: string) => {
    if (key === selKey) return close()
    if (key === rootKey) {
      setRootOpen(true)
      setUnitParam(null)
      return
    }
    const node = tree.byId.get(key)
    if (node) {
      if (desktop) {
        // Chưa hiện trên sơ đồ (bấm cấp trên / cấp dưới trong ngăn) → mở đường tới nó, thoát tìm nếu nó không nằm
        // trong kết quả
        if (!networkDrillShows(columns, key)) {
          go(networkDrillReveal(ix, node), true)
          clearFilters()
        }
      } else {
        const ancestors = networkAncestors(node)
        if (ancestors.some((a) => !open.has(a.unit.id))) setOpenIds(new Set([...open, ...ancestors.map((a) => a.unit.id)]))
      }
    }
    setRootOpen(false)
    setUnitParam(key)
  }
  const expandAll = () => setOpenIds(networkOpenToDepth(tree, Infinity))
  const collapseAll = () => setOpenIds(new Set())
  const allOpen = useMemo(() => [...tree.byId.values()].every((n) => !n.children.length || open.has(n.unit.id)), [tree, open])
  /** Bấm ô trên dải thống kê: lọc theo ô đó, bấm lại = Tất cả. */
  const pickFilter = (f: NetworkFilter) => setFilter((cur) => nextNetworkFilter(cur, f))

  /** Bấm thân một ô trên sơ đồ (không phải nút ⓘ). `col` = cột của ô. */
  const activate = (col: number, item: NetworkDrillItem<U>) => {
    switch (item.kind) {
      case 'root':
        return select(rootKey)
      case 'results':
        return clearFilters()
      case 'group':
        return go(networkDrillClick(drill, col, item.key))
      case 'more':
        if (item.group === NETWORK_DRILL_RESULTS) return setResPage({ match, shown: item.shown + NETWORK_DRILL_STEP })
        return go({ path: drill.path, shown: { ...drill.shown, [item.group]: item.shown + NETWORK_DRILL_STEP } }, true)
      case 'unit': {
        const node = item.node
        if (item.result) {
          // Kết quả tìm: nhảy vào cây, mở luôn cấp dưới; không có cấp dưới thì mở ngăn chi tiết
          go(networkDrillOpenTo(ix, node), true)
          clearFilters()
          if (node.children.length) close()
          else {
            setRootOpen(false)
            setUnitParam(node.unit.id)
          }
          return
        }
        if (node.children.length) return go(networkDrillClick(drill, col, item.key, networkDrillSoleGroup(ix, node.unit.id)))
        return select(node.unit.id)
      }
      case 'empty':
        return
    }
  }

  const atStart = networkDrillSame(drill, start)
  /** Quay lại: đang tìm / lọc thì thoát ra; không thì về bước trước. */
  const back = () => {
    if (filtering) return clearFilters()
    const prev = hist[hist.length - 1]
    if (!prev) return
    setHist(hist.slice(0, -1))
    setDrillRaw(prev)
    close()
  }
  /** Đóng hết: về lúc mới mở trang (vẫn Quay lại được). */
  const closeAll = () => {
    if (!atStart) setHist((h) => [...h.slice(-(HISTORY_MAX - 1)), drill])
    setDrillRaw(null)
    clearFilters()
    close()
  }
  /** Bấm một chặng trên vạch đường đi: về đúng ô đó (`i` = vị trí trên đường mở, -1 = gốc). */
  const crumb = (i: number) => {
    if (filtering) return clearFilters()
    go(i < 0 ? start : { path: drill.path.slice(0, i + 1), shown: drill.shown })
  }

  // Khối kết quả: tìm chữ thì ghi chữ đang tìm, lọc thì tên bộ lọc
  const q = text.trim()
  const resultsHead = q
    ? { label: `Tìm “${q}”`, short: '' }
    : filter === 'issues'
      ? { label: 'Cần xử lý', short: '!' }
      : filter !== 'all'
        ? { label: NETWORK_TYPE_META[filter].label, short: NETWORK_TYPE_META[filter].short }
        : { label: '', short: '' }

  return {
    open, text, setText, filter, setFilter, pickFilter, stats, match, filtering, hits,
    sel, selKey, shownKey, shownNode,
    select, close, toggle, expandAll, collapseAll, allOpen,
    /** Sơ đồ theo nhóm (máy tính). */
    drill: {
      ix,
      columns,
      /** Nhãn + chữ tắt của khối kết quả (chữ tắt rỗng = biểu tượng tìm). */
      resultsHead,
      activate,
      /** Nút ⓘ trên ô đơn vị: mở / đóng ngăn chi tiết. */
      info: (node: NetworkNode<U>) => select(node.unit.id),
      back,
      closeAll,
      crumb,
      canBack: filtering || hist.length > 0,
      canCloseAll: filtering || !atStart || !!selKey,
    },
  }
}

export type NetworkDrillView<U extends NetworkUnitBase & { orders30d?: number | null }> = ReturnType<typeof useNetworkTreeState<U>>['drill']
